import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import FormDots from '../common/FormDots';
import { Button, Col, Input, InputNumber, Row } from 'antd';
import { Form } from '@ant-design/compatible';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { addItem, getFixedFloatSum, removeItem } from '../../utils/InvoiceUtils';

require('./BuyerInvoiceForm.less');

const PartFormFields: FC<any> = ({
	formData,
	form,
	currency,
	initialUuid,
	state,
	setState,
	recalculateTotals,
}): React.ReactElement => {
	const [, setUuid] = useState(initialUuid);

	const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

	const partsKeys = getFieldValue('partsKeys') || [];

	const handlePartChange = useCallback(
		(val, k, keyName) => {
			const partUnitPrice = keyName === 'unitPrice' ? val : getFieldValue(`parts-${k}-unitPrice`);
			const partQuantity = keyName === 'quantity' ? val : getFieldValue(`parts-${k}-quantity`);
			const partsKeys = getFieldValue(`partsKeys`);
			let amount = 0;
			if (partUnitPrice && partQuantity) {
				amount = partUnitPrice * partQuantity;
				setFieldsValue({ [`parts-${k}-amount`]: amount.toString() });
			} else {
				setFieldsValue({ [`parts-${k}-amount`]: undefined });
			}
			const partsTotalBeforeTax = partsKeys.reduce((acc, k2) => {
				const price = k2 === k ? amount : getFieldValue(`parts-${k2}-amount`) || '0';
				return getFixedFloatSum(acc, price);
			}, 0);

			setFieldsValue({ partsTotalBeforeTax: partsTotalBeforeTax });
			recalculateTotals();
		},
		[getFieldValue, recalculateTotals, setFieldsValue]
	);

	const partsFormItems = useMemo(
		() =>
			partsKeys.map((k, partsIndex) => {
				getFieldDecorator(`parts-${k}-partId`, {
					initialValue: nullSafeGet(`parts-${k}-partId`, formData),
				});
				return (
					<Row key={`part-line-item-${k}`}>
						<Col span={1}>
							<div
								style={
									partsIndex === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
								}
							>
								{partsIndex + 1}
							</div>
						</Col>

						<Col span={5}>
							<Form.Item label={partsIndex === 0 ? 'Description' : ''}>
								{getFieldDecorator(`parts-${k}-description`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`parts-${k}-description`, formData) ||
										nullSafeGet(`partItemInitialValues.parts-${k}-description`, state),
									rules: [
										{
											required: true,
											whitespace: true,
											message: 'Please add description.',
										},
									],
								})(<Input />)}
							</Form.Item>
						</Col>
						<Col span={3} offset={1}>
							<Form.Item label={partsIndex === 0 ? 'Part Number' : ''}>
								{getFieldDecorator(`parts-${k}-partNumber`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`parts-${k}-partNumber`, formData) ||
										nullSafeGet(`partItemInitialValues.parts-${k}-partNumber`, state)
											? (
													nullSafeGet(`parts-${k}-partNumber`, formData) ||
													nullSafeGet(`partItemInitialValues.parts-${k}-partNumber`, state)
											  ).toString()
											: undefined,
								})(<Input />)}
							</Form.Item>
						</Col>

						<Col span={2} offset={1}>
							<Form.Item label={partsIndex === 0 ? 'Unit Type' : ''}>
								{getFieldDecorator(`parts-${k}-unitType`, {
									validateTrigger: ['onBlur'],
									initialValue: nullSafeGetOrElse(`parts-${k}-unitType`, formData, 'each'),
									rules: [
										{
											required: true,
											message: 'Please add unit of measure.',
										},
									],
								})(<Input />)}
							</Form.Item>
						</Col>

						<Col span={2} offset={1}>
							<Form.Item label={partsIndex === 0 ? 'Quantity' : ''}>
								{getFieldDecorator(`parts-${k}-quantity`, {
									initialValue:
										nullSafeGet(`parts-${k}-quantity`, formData) ||
										nullSafeGet(`partItemInitialValues.parts-${k}-quantity`, state),
									validateTrigger: ['onBlur'],
									rules: [
										{
											required: true,
											message: 'Please add quantity.',
										},
									],
								})(
									<InputNumber
										onChange={(e) => handlePartChange(e, k, 'quantity')}
										style={{ width: '100%' }}
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={3} offset={1}>
							<Form.Item label={partsIndex === 0 ? 'Unit Price' : ''}>
								{getFieldDecorator(`parts-${k}-unitPrice`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`parts-${k}-unitPrice`, formData) ||
										nullSafeGet(`partItemInitialValues.parts-${k}-unitPrice`, state),
									rules: [
										{
											required: true,
											message: 'Please add price.',
										},
									],
								})(
									<InputNumber
										step={0.01}
										onChange={(e) => handlePartChange(e, k, 'unitPrice')}
										style={{ width: '100%' }}
										addonAfter={currency.id}
									/>
								)}
							</Form.Item>
						</Col>
						<Col
							span={1}
							style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
						>
							<div
								style={
									partsIndex === 0
										? {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												marginTop: 30,
										  }
										: {
												height: 40,
												display: 'flex',
												alignItems: 'center',
										  }
								}
							>
								{partsKeys.length > 0 ? (
									<MinusCircleOutlined
										className="dynamic-delete-button"
										onClick={() => removeItem(form, recalculateTotals)('partsKeys', 'parts', k)}
									/>
								) : null}
							</div>
						</Col>
						<Col span={1} offset={2}>
							<Form.Item>
								<div
									style={
										partsIndex === 0
											? {
													height: 40,
													display: 'flex',
													justifyContent: 'flex-end',
													alignItems: 'center',
													marginTop: 30,
											  }
											: {
													height: 40,
													display: 'flex',
													justifyContent: 'flex-end',
													alignItems: 'center',
											  }
									}
								>
									{getFieldDecorator(`parts-${k}-amount`, {
										initialValue:
											formData[`parts-${k}-amount`] ||
											nullSafeGet(`partItemInitialValues.parts-${k}-amount`, state),
									})}
									{currency.format(getFieldValue(`parts-${k}-amount`))}
								</div>
							</Form.Item>
						</Col>
					</Row>
				);
			}),
		[
			currency,
			form,
			formData,
			getFieldDecorator,
			getFieldValue,
			handlePartChange,
			partsKeys,
			recalculateTotals,
			state,
		]
	);

	const onAddItem = useCallback(() => {
		setUuid((uuid) => addItem(form, state, setState)('partsKeys', uuid, 'partsTotalBeforeTax'));
	}, [form, setState, state]);

	return (
		<div style={{ marginBottom: 32 }}>
			<div className="buyerInvoiceForm__sectionHead">
				<div className="buyerInvoiceForm__sectionHeadPrimary">Parts</div>
				<FormDots />
				{nullSafeGetOrElse('length', partsKeys, 0) > 0 ? (
					getFieldDecorator(`partsTotalBeforeTax`, {
						validateTrigger: ['onBlur'],
						initialValue: `${nullSafeGetOrElse(`partsTotalBeforeTax`, formData, 0)}`,
					})(
						<div className="buyerInvoiceForm__sectionHeadSecondary">
							{currency.format(getFieldValue(`partsTotalBeforeTax`))}
						</div>
					)
				) : (
					<div className="buyerInvoiceForm__sectionHeadSecondary">
						{getFieldDecorator(`partsTotalBeforeTax`, {
							validateTrigger: ['onBlur'],
							initialValue: `${nullSafeGetOrElse(`partsTotalBeforeTax`, formData, 0)}`,
						})(
							<InputNumber
								step={1}
								onChange={recalculateTotals}
								style={{ width: 240 }}
								addonAfter={currency.id}
							/>
						)}
					</div>
				)}
			</div>
			{partsFormItems}
			<Row>
				<Col span={19} offset={1}>
					<Button type="dashed" onClick={onAddItem} style={{ height: 32 }}>
						<PlusOutlined /> Add part
					</Button>
				</Col>
			</Row>
		</div>
	);
};

export default PartFormFields;
